import React, { useMemo, useState, useEffect, useCallback } from 'react';
import MaterialTable from 'material-table';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Box, Grid, Typography, Button, CircularProgress } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import PhonelinkOffOutlinedIcon from '@mui/icons-material/PhonelinkOffOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import C2Dialog, { C2DangerButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2AddButton,
  C2EmptyState,
  C2DocsChip,
  C2Select,
  C2StatusChip,
  C2SummaryCard,
  C2ProgressBar,
} from '../components/Table/C2UserTable';
import {
  PUSH_PLATFORMS,
  PLATFORM_LABELS,
  USERTYPE_LABELS,
  DEVICETYPE_LABELS,
  DELIVERY_STATUS_UI,
  pushCoverage,
  platformReport,
  deliveryTotals,
  deliveryStatus,
  pushErrorHelp,
} from '../components/notifications/pushReport';

const PLATFORM_ICONS = { ANDROID: AndroidIcon, IOS: AppleIcon, UNKNOWN: PhonelinkOffOutlinedIcon };
const PLATFORM_COLORS = { ANDROID: '#15803D', IOS: C2.sidebar, UNKNOWN: C2.muted };

const pct = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)}%` : '—');

function PlatformCount({ platform, value }) {
  const Icon = PLATFORM_ICONS[platform];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mr: 1.25 }}>
      <Icon sx={{ fontSize: 16, color: PLATFORM_COLORS[platform] }} />
      <Typography component="span" sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', fontWeight: 700, color: C2.sidebar }}>
        {value}
      </Typography>
    </Box>
  );
}

function CoverageRow({ label, coverage }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: `1px solid ${C2.cellBorder}`, flexWrap: 'wrap' }}>
      <Box sx={{ minWidth: 140 }}>
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{label}</Typography>
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>{coverage.users} cuentas</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <PlatformCount platform="ANDROID" value={coverage.ANDROID} />
        <PlatformCount platform="IOS" value={coverage.IOS} />
        {coverage.UNKNOWN ? <PlatformCount platform="UNKNOWN" value={coverage.UNKNOWN} /> : null}
      </Box>
      <Box sx={{ minWidth: 170, flex: '0 1 220px' }}>
        <C2ProgressBar used={coverage.devices} total={coverage.users} />
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: coverage.withoutDevice ? '#B45309' : C2.muted, mt: 0.25 }}>
          {coverage.withoutDevice ? `${coverage.withoutDevice} sin notificaciones activas` : 'Todos con notificaciones activas'}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const notificationdata = useSelector((s) => s.notificationdata);
  const usersdata = useSelector((s) => s.usersdata);
  const users = useMemo(() => usersdata?.users || usersdata?.staticusers || [], [usersdata]);

  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [notice, setNotice] = useState(state?.sent ? { severity: state.sent.severity || 'success', msg: state.sent.msg } : null);
  const [refreshing, setRefreshing] = useState(false);
  const isRefreshing = refreshing || Boolean(usersdata?.loading);

  useEffect(() => {
    if ((!users || users.length === 0) && typeof api.fetchUsersOnce === 'function') {
      dispatch(api.fetchUsersOnce({ includeLocations: false }));
    }
  }, [dispatch, users]);

  const handleRefreshCoverage = useCallback(async () => {
    setRefreshing(true);
    try {
      if (typeof api.fetchUsers === 'function') {
        dispatch(api.fetchUsers());
      }
      if (typeof api.fetchUsersOnce === 'function') {
        dispatch(api.fetchUsersOnce({ force: true, includeLocations: false }));
      }
      if (typeof api.fetchNotifications === 'function') {
        dispatch(api.fetchNotifications());
      }
    } catch (err) {
      console.warn('Error al actualizar cobertura:', err);
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 800);
    }
  }, [dispatch]);

  const rows = useMemo(
    () =>
      [...(notificationdata.notifications || [])]
        .map((n) => ({ ...n, deliveryStatus: deliveryStatus(n) }))
        .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0)),
    [notificationdata.notifications]
  );

  const coverage = useMemo(
    () => ({
      driver: pushCoverage(users, 'driver'),
      customer: pushCoverage(users, 'customer'),
      all: pushCoverage(users, 'All'),
    }),
    [users]
  );

  const summary = useMemo(() => {
    const monthStart = moment().startOf('month').valueOf();
    const month = rows.filter((r) => (Number(r.createdAt) || 0) >= monthStart);
    const rate = (p) => {
      let delivered = 0;
      let attempted = 0;
      rows.forEach((r) => {
        if (!r.audience || r.deliveryStatus === 'sending' || r.deliveryStatus === 'verifying') return;
        const rep = platformReport(r, p);
        delivered += rep.delivered;
        attempted += rep.delivered + rep.failed + rep.expired;
      });
      return { delivered, attempted };
    };
    return {
      sentMonth: month.length,
      devicesMonth: month.reduce((acc, r) => acc + (Number(r.audience?.devices) || 0), 0),
      android: rate('ANDROID'),
      ios: rate('IOS'),
    };
  }, [rows]);

  const filtered = rows.filter((r) => !statusFilter || r.deliveryStatus === statusFilter);
  const current = openId ? rows.find((r) => r.id === openId) : null;
  const toDelete = deleteId ? rows.find((r) => r.id === deleteId) : null;

  const goToCreate = (template) => navigate('/notifications/addnotifications', { state: template ? { template } : undefined });

  const confirmDelete = () => {
    if (!toDelete) return;
    dispatch(api.editNotifications(toDelete, 'Delete'));
    setNotice({ severity: 'success', msg: `Se eliminó "${toDelete.title}" del historial.` });
    setDeleteId(null);
    if (openId === toDelete.id) setOpenId(null);
  };

  const columns = [
    {
      title: 'Fecha',
      field: 'createdAt',
      render: (r) => (
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.85rem', color: C2.sidebar }}>{r.createdAt ? moment(r.createdAt).format('lll') : '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>{r.createdAt ? moment(r.createdAt).fromNow() : ''}</Typography>
        </Box>
      ),
    },
    {
      title: 'Mensaje',
      field: 'title',
      customFilterAndSearch: (term, r) => `${r.title || ''} ${r.body || ''}`.toLowerCase().includes(String(term).toLowerCase()),
      render: (r) => (
        <Box sx={{ textAlign: 'left', maxWidth: 380 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.title || 'Sin título'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
            {r.body || ''}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Destinatarios',
      field: 'usertype',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.84rem', color: C2.sidebar }}>
            {USERTYPE_LABELS[r.usertype] || r.usertype || '—'}
          </Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>
            {DEVICETYPE_LABELS[r.devicetype] || r.devicetype || '—'}
            {r.cityName && r.cityId && r.cityId !== 'All' ? ` · 📍 ${r.cityName}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Dispositivos',
      field: 'audience',
      sorting: false,
      render: (r) =>
        r.audience ? (
          <Box sx={{ textAlign: 'left' }}>
            <Box>
              <PlatformCount platform="ANDROID" value={Number(r.audience.ANDROID) || 0} />
              <PlatformCount platform="IOS" value={Number(r.audience.IOS) || 0} />
              {Number(r.audience.UNKNOWN) ? <PlatformCount platform="UNKNOWN" value={Number(r.audience.UNKNOWN)} /> : null}
            </Box>
            {Number(r.audience.withoutDevice) ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: '#B45309' }}>{r.audience.withoutDevice} sin app activa</Typography>
            ) : null}
          </Box>
        ) : (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted }}>—</Typography>
        ),
    },
    {
      title: 'Entrega',
      field: 'deliveryStatus',
      render: (r) => {
        const ui = DELIVERY_STATUS_UI[r.deliveryStatus];
        const totals = deliveryTotals(r);
        return (
          <Box sx={{ textAlign: 'left', minWidth: 150 }}>
            <C2StatusChip tone={ui.tone} label={ui.label} />
            {r.audience && Number(r.audience.devices) ? (
              <Box sx={{ mt: 0.75 }}>
                <C2ProgressBar used={r.deliveryStatus === 'sending' || r.deliveryStatus === 'verifying' ? totals.accepted : totals.delivered} total={Number(r.audience.devices)} minWidth={130} />
              </Box>
            ) : null}
          </Box>
        );
      },
    },
  ];

  const toolbar = c2Toolbar(
    <C2Select
      key="status"
      id="notifications-status"
      label="Entrega"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={180}
      options={[{ value: '', label: 'Todas' }, ...Object.keys(DELIVERY_STATUS_UI).map((k) => ({ value: k, label: DELIVERY_STATUS_UI[k].label }))]}
    />,
    <C2AddButton key="add" label="Nueva notificación" onClick={() => goToCreate()} />
  );

  if (notificationdata.loading && !notificationdata.notifications) return <CircularLoading />;

  const currentTotals = current ? deliveryTotals(current) : null;
  const currentStatus = current ? DELIVERY_STATUS_UI[current.deliveryStatus] : null;

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {notice.msg}
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={SendOutlinedIcon} label="Enviadas este mes" value={summary.sentMonth} caption={`${summary.devicesMonth} dispositivos alcanzados`} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={NotificationsActiveOutlinedIcon} label="Con notificaciones activas" value={`${coverage.all.devices}`} caption={`de ${coverage.all.users} cuentas (${pct(coverage.all.devices, coverage.all.users)})`} accent="#16A34A" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={AndroidIcon} label="Entrega en Android" value={pct(summary.android.delivered, summary.android.attempted)} caption={summary.android.attempted ? `${summary.android.delivered} de ${summary.android.attempted} confirmadas` : 'Aún sin envíos verificados'} accent="#15803D" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={AppleIcon} label="Entrega en iOS" value={pct(summary.ios.delivered, summary.ios.attempted)} caption={summary.ios.attempted ? `${summary.ios.delivered} de ${summary.ios.attempted} confirmadas` : 'Aún sin envíos verificados'} accent={C2.sidebar} />
          </Grid>
        </Grid>

        <C2TableCard sx={{ mb: 2 }}>
          <C2SectionHeading
            title="Cobertura actual"
            subtitle="Cuántas cuentas pueden recibir notificaciones hoy. Las que no tienen notificaciones activas nunca abrieron la app, denegaron el permiso o cerraron sesión; solo verán el mensaje en la bandeja de la app."
            action={
              <Button
                size="small"
                variant="outlined"
                disabled={isRefreshing}
                onClick={handleRefreshCoverage}
                startIcon={
                  isRefreshing ? (
                    <CircularProgress size={14} sx={{ color: C2.violet }} />
                  ) : (
                    <RefreshRoundedIcon sx={{ fontSize: 18, color: C2.violet }} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontFamily: C2_FONT,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: C2.violet,
                  backgroundColor: '#FFFFFF',
                  border: '1.5px solid #D7CFEB',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.5,
                  boxShadow: '0 2px 6px rgba(100,17,237,0.06)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: C2.lavender,
                    borderColor: C2.violet,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(100,17,237,0.14)',
                  },
                  '&.Mui-disabled': {
                    color: C2.muted,
                    borderColor: '#EDE8F7',
                    backgroundColor: '#FAFAFD',
                  },
                }}
              >
                {isRefreshing ? 'Actualizando…' : 'Actualizar'}
              </Button>
            }
          />
          <Box sx={{ px: 0.5 }}>
            <CoverageRow label="Conductores" coverage={coverage.driver} />
            <CoverageRow label="Usuarios" coverage={coverage.customer} />
          </Box>
        </C2TableCard>

        <C2TableCard>
          <C2SectionHeading title="Historial de envíos" subtitle="La entrega se confirma con Apple (iOS) y Google (Android) unos minutos después del envío; el estado se actualiza solo." />
          <MaterialTable
            title={t('push_notification_title')}
            columns={columns}
            data={filtered}
            style={c2TableStyle(isRTL)}
            components={toolbar}
            onRowClick={(evt, row) => {
              setSelectedRow(row.tableData.id);
              setOpenId(row.id);
            }}
            options={{
              ...c2TableOptions(selectedRow),
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
            }}
            localization={c2Localization(
              localization(t),
              <C2EmptyState
                icon={NotificationsActiveOutlinedIcon}
                title={statusFilter ? 'No hay envíos con este estado' : 'Aún no has enviado notificaciones'}
                message="Crea una notificación para avisar a conductores o usuarios de promociones, cambios o novedades de Conecta2."
              />
            )}
            actions={[
              {
                icon: () => <C2DocsChip icon={InsightsOutlinedIcon} label="Detalle" />,
                tooltip: 'Ver entrega por plataforma',
                onClick: (e, r) => setOpenId(r.id),
              },
              {
                icon: () => <ContentCopyRoundedIcon sx={{ color: C2.violet, fontSize: 20 }} />,
                tooltip: 'Usar como plantilla',
                onClick: (e, r) => goToCreate({ title: r.title, body: r.body, usertype: r.usertype, devicetype: r.devicetype }),
              },
              {
                icon: () => <DeleteOutlineRoundedIcon sx={{ color: '#B91C1C', fontSize: 20 }} />,
                tooltip: 'Eliminar del historial',
                onClick: (e, r) => setDeleteId(r.id),
              },
            ]}
          />
        </C2TableCard>

        <C2Dialog
          open={!!current}
          onClose={() => setOpenId(null)}
          title={current ? current.title || 'Notificación' : 'Notificación'}
          icon={NotificationsActiveOutlinedIcon}
          maxWidth={760}
          actions={
            <>
              <C2SecondaryButton onClick={() => current && goToCreate({ title: current.title, body: current.body, usertype: current.usertype, devicetype: current.devicetype })}>
                Usar como plantilla
              </C2SecondaryButton>
              <C2SecondaryButton onClick={() => setOpenId(null)}>Cerrar</C2SecondaryButton>
            </>
          }
        >
          {current ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <C2StatusChip tone={currentStatus.tone} label={currentStatus.label} />
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>
                  {current.createdAt ? moment(current.createdAt).format('lll') : ''} · {USERTYPE_LABELS[current.usertype] || current.usertype} · {DEVICETYPE_LABELS[current.devicetype] || current.devicetype}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: '14px', backgroundColor: C2.lavender, border: `1px solid ${C2.border}`, mb: 2 }}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.92rem', color: C2.sidebar }}>{current.title}</Typography>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: C2.sidebar, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{current.body}</Typography>
              </Box>

              {!current.audience ? (
                <Alert severity="info" sx={{ borderRadius: '12px', fontFamily: C2_FONT }}>
                  Este envío se hizo antes de activar el seguimiento de entrega, por eso no tiene reporte por plataforma.
                </Alert>
              ) : (
                <>
                  <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, mb: 0.75 }}>Entrega por plataforma</Typography>
                  <Box sx={{ borderRadius: '12px', border: `1px solid ${C2.border}`, overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr repeat(4, 1fr)', px: 1.5, py: 1, backgroundColor: C2.lavender }}>
                      {['Plataforma', 'Dispositivos', 'Entregadas', 'Fallidas', 'Pendientes'].map((h) => (
                        <Typography key={h} sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.74rem', color: C2.muted, textTransform: 'uppercase' }}>{h}</Typography>
                      ))}
                    </Box>
                    {PUSH_PLATFORMS.filter((p) => p !== 'UNKNOWN' || platformReport(current, p).targeted).map((p) => {
                      const rep = platformReport(current, p);
                      const Icon = PLATFORM_ICONS[p];
                      return (
                        <Box key={p} sx={{ display: 'grid', gridTemplateColumns: '1.3fr repeat(4, 1fr)', alignItems: 'center', px: 1.5, py: 1, borderTop: `1px solid ${C2.cellBorder}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Icon sx={{ fontSize: 18, color: PLATFORM_COLORS[p] }} />
                            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar }}>{PLATFORM_LABELS[p]}</Typography>
                          </Box>
                          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: C2.sidebar }}>{rep.targeted}</Typography>
                          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: '#15803D', fontWeight: 700 }}>
                            {rep.delivered} <Box component="span" sx={{ color: C2.muted, fontWeight: 400, fontSize: '0.74rem' }}>({pct(rep.delivered, rep.targeted)})</Box>
                          </Typography>
                          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: rep.failed + rep.expired ? '#B91C1C' : C2.sidebar, fontWeight: rep.failed + rep.expired ? 700 : 400 }}>{rep.failed + rep.expired}</Typography>
                          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: rep.pending ? C2.violet : C2.sidebar }}>{rep.pending}</Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>Cuentas seleccionadas</Typography>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: C2.sidebar }}>{Number(current.audience.users) || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>Dispositivos</Typography>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: C2.sidebar }}>{Number(current.audience.devices) || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>Sin app activa</Typography>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: Number(current.audience.withoutDevice) ? '#B45309' : C2.sidebar }}>{Number(current.audience.withoutDevice) || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>Confirmadas</Typography>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: '#15803D' }}>{currentTotals.delivered}</Typography>
                    </Grid>
                  </Grid>

                  {Number(current.audience.unknownPlatform) ? (
                    <Alert severity="warning" sx={{ borderRadius: '12px', fontFamily: C2_FONT, mb: 1.5 }}>
                      {current.audience.unknownPlatform} cuentas quedaron fuera porque no registran si usan Android o iOS. Se incluirán cuando vuelvan a abrir la app, o si envías a "Todos los dispositivos".
                    </Alert>
                  ) : null}

                  {current.errors && Object.keys(current.errors).length ? (
                    <>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, mb: 0.75 }}>Motivos de falla</Typography>
                      {Object.keys(current.errors).map((reason) => (
                        <Box key={reason} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, mb: 0.75, borderRadius: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
                          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: '#B91C1C', minWidth: 32 }}>{current.errors[reason]}</Typography>
                          <Box>
                            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.82rem', color: '#991B1B' }}>{reason}</Typography>
                            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: '#7F1D1D' }}>{pushErrorHelp(reason)}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </>
                  ) : null}

                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted, mt: 1 }}>
                    "Entregada" significa que Apple o Google aceptaron el mensaje para ese celular. Si la persona desactivó las notificaciones de Conecta2 en los ajustes del teléfono, el sistema no la muestra.
                    {current.verifiedAt ? ` Última verificación: ${moment(current.verifiedAt).format('lll')}.` : ''}
                  </Typography>
                </>
              )}
            </Box>
          ) : null}
        </C2Dialog>

        <C2Dialog
          open={!!toDelete}
          onClose={() => setDeleteId(null)}
          title="Eliminar del historial"
          icon={DeleteOutlineRoundedIcon}
          actions={
            <>
              <C2SecondaryButton onClick={() => setDeleteId(null)}>Cancelar</C2SecondaryButton>
              <C2DangerButton onClick={confirmDelete}>Eliminar</C2DangerButton>
            </>
          }
        >
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.9rem', color: C2.sidebar }}>
            Se borrará "{toDelete?.title}" y su reporte de entrega. Las personas que ya la recibieron la conservan en su celular.
          </Typography>
        </C2Dialog>
      </ThemeProvider>
    </Box>
  );
}
